import * as THREE from 'three';
import { cellCenter, kindleCone, type BattleState, type CanalRules, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Way of the Altars (docs/sendas/paladin.md, chapter 1), for the paladin's path: open, white, still water, and black
 * glass where the sea comes in (never the warrior's snow). Each place:
 *
 * - `glassShore` (acts 1 and 3, and the close): the Burn's glass going into a pale still sea on its far rim and both
 *   flanks; the canals the first Vigil cut in it (dark grooves), and in them the water each wave brings, a silver thread
 *   that runs a cell at a time as the sim's front does and dims as it dries; the stones at the forks (light stone, each
 *   sitting in the mouth it shuts), the pools in their basins with the water's level, the altars with their carved eye
 *   that opens white and red when its pool is full; the high pool's rock (the test), its mouth's slab. The sea's foam
 *   on the rim, a white line rolling in `warn` before each wave; the burnt lines of the crías and the Arado, ember on the
 *   glass, and the Arado's three lanes marked red while its blades are down.
 * - `watchtower` (act 2, and its scene): the first Vigil's watchtower, tall pale stone broken at the top, molten
 *   ballistas fused into the glass; the second altar in the middle, its eye kindling as the fight's clock fills; the
 *   drake's cone marked red on the glass while it draws breath.
 * - `glassNight` (the campfire): the glass at night under stars, the stars in it (one sheet over the ground).
 * - `temple`, `keepYard` (the opening): the bastion's yard at dawn in cold grey flags, an old altar stone with the eye;
 *   the keep behind it where there is no Temple.
 *
 * Draw calls: the dressing (one merged mesh), the grooves (one), the water threads (one, its wetness a vertex attribute
 * written when it changes), the sea and its foam (two), the pools' water (one each, two at most), the stones (three),
 * the burns and lanes (one instanced), the cone (one), the eyes' glow layer; the sky dome; at night the stars (two).
 * No lights of its own: the board's are repainted, pale and cool (HOURS).
 */

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const WATER = new THREE.Color('#e8f1f7');
const POOL_GLOW = new THREE.Color('#bfe0ff');
const LAMP = new THREE.Color('#dce8ff');
const STONE = ['#c9cdd3', '#b9bec6', '#d6d9de'];
const GLASS_DARK = ['#0a090e', '#121018', '#17151d'];
const FLAG = ['#5a5f68', '#666b74', '#50555e'];

export const ALTAR_PLACES = new Set(['glassShore', 'watchtower', 'glassNight', 'temple', 'keepYard']);

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/**
 * The Way's light by the hour: pale and open, the sky white over still water; dawn a pearl hour with a cold sun low on
 * the side (docs/sendas/paladin.md, chapter 1: «el sol bajo del amanecer de costado»), never gold.
 */
const HOURS: Record<Hour, { bg: string; fog: string; low: string; mid: string; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  day: { bg: '#c3ccd6', fog: '#c0c9d3', low: '#e9edf2', mid: '#d3dae2', hemi: ['#e4eaf2', '#2c2e38', 2.4], key: ['#fffaf4', 3.1], rim: ['#b4c8ee', 1.5], sky: ['#7c8ea6', '#e9eef3', '#ffffff'] },
  dawn: { bg: '#cfd8e1', fog: '#d3dbe3', low: '#d6dce4', mid: '#bec6d0', hemi: ['#d5dde9', '#23252e', 2.1], key: ['#fff1ea', 3.3], rim: ['#a9bfee', 1.7], sky: ['#6a7d96', '#f1f4f7', '#fff6f0'] },
  dusk: { bg: '#4b5568', fog: '#525c70', low: '#6d7688', mid: '#566073', hemi: ['#a0aec8', '#16181f', 1.8], key: ['#d9e0f2', 2.0], rim: ['#8aa4e6', 1.5], sky: ['#1a2133', '#6c778d', '#d8def0'] },
  night: { bg: '#05070c', fog: '#0b0f18', low: '#151b26', mid: '#0e131c', hemi: ['#7183ad', '#0b0c10', 1.15], key: ['#a9bbe6', 0.9], rim: ['#7a96ff', 1.25], sky: ['#020409', '#0f1520', '#9fb2e2'] },
};

/**
 * Volcanic glass, glossy and calm: the board's own material with long soft swells in its normal (large facets, not
 * shards), a cold sky caught in it at a grazing look (fresnel) and a moving sheen; the walkable board lighter than
 * what lies beyond (its vertex colours).
 */
const glassMaterial = (src: THREE.MeshStandardMaterial, u: { uSky: THREE.IUniform<THREE.Color>; uTime: THREE.IUniform<number> }): THREE.MeshStandardMaterial => {
  const m = src.clone();
  m.roughness = 0.16;
  m.metalness = 0.3;
  m.flatShading = false;
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vGlW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvGlW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform vec3 uSky;
uniform float uTime;
varying vec3 vGlW;
float glH(vec2 p) { p = fract(p * vec2(0.1031, 0.103)); p += dot(p, p.yx + 33.33); return fract((p.x + p.y) * p.x); }
float glN(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 w = f * f * (3.0 - 2.0 * f);
  return mix(mix(glH(i), glH(i + vec2(1.0, 0.0)), w.x), mix(glH(i + vec2(0.0, 1.0)), glH(i + vec2(1.0, 1.0)), w.x), w.y); }`,
      )
      .replace(
        '#include <normal_fragment_maps>',
        `#include <normal_fragment_maps>
{
  vec2 q = mod(vGlW.xz, 289.0) * 0.16;
  float a = glN(q), b = glN(q + vec2(5.2, 1.3));
  vec3 tilt = vec3(a - 0.5, 0.0, b - 0.5) * 0.18;
  normal = normalize(normal + (viewMatrix * vec4(tilt, 0.0)).xyz);
}`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
{
  float fr = pow(1.0 - clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0), 3.0);
  float sheen = 0.5 + 0.5 * sin(dot(vGlW.xz, vec2(0.21, 0.13)) + mod(uTime, 120.0) * 0.25);
  totalEmissiveRadiance += uSky * (0.05 + fr * 0.55) * (0.8 + 0.2 * sheen);
}`,
      );
  };
  m.customProgramCacheKey = () => 'somnhold-altars-glass';
  m.needsUpdate = true;
  return m;
};

/** The pale still sea, a slow swell, the light off it at a grazing look; the wave rolling in as a white line. */
const seaMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      time: { value: 0 },
      // Where the incoming wave's line is: 0 far out, 1 at the rim (−1: none).
      wave: { value: -1 },
      edge: { value: 6.6 * S },
      deep: { value: new THREE.Color('#c9d4de') },
      shallow: { value: new THREE.Color('#8fa3b4') },
      glint: { value: new THREE.Color('#ffffff') },
      night: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vW;
      uniform float time;
      void main() {
        vec4 w = modelMatrix * vec4(position, 1.0);
        w.y += 0.02 * sin(w.x * 0.9 + time * 0.8) * cos(w.z * 0.7 - time * 0.6);
        vW = w.xyz;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      varying vec3 vW;
      uniform float time;
      uniform float wave;
      uniform float edge;
      uniform vec3 deep;
      uniform vec3 shallow;
      uniform vec3 glint;
      uniform float night;
      void main() {
        // How far out of the board (hex distance, world units): the foam follows its edge, not a circle.
        // (The same wandering coast the glass slopes under, altars.ts apply(): not the board's hexagon.)
        float rd = length(vW.xz) / ${S.toFixed(4)} + 0.45 * sin(atan(vW.z, vW.x) * 7.0 + 0.7) + 0.25 * sin(vW.x * 0.9 + vW.z * 0.5);
        float d = rd * ${S.toFixed(4)};
        // Inland (the Burn behind the near rim) there is no sea.
        if (vW.z > ${(4.2 * 0.866 * S).toFixed(4)}) discard;
        float out_ = clamp((d - edge) / (edge * 3.5), 0.0, 1.0);
        vec3 c = mix(shallow, deep, out_);
        // Still water: long soft ripples, a sheen where they catch the light.
        // Open water to a white horizon: a gentle swell in long bands, the low sun's path glittering on it.
        float swell = sin(vW.z * 0.35 + vW.x * 0.08 - time * 0.6) * 0.5 + 0.5;
        float swell2 = sin(vW.z * 0.9 - vW.x * 0.3 - time * 1.1) * 0.5 + 0.5;
        c = mix(c, c * 0.9, swell * 0.35);
        c += vec3(0.04) * swell2;
        float path = exp(-pow(vW.x * 0.05 - 0.9, 2.0) * 3.0) * smoothstep(0.0, 1.0, out_);
        float glit = step(0.93, fract(sin(dot(floor(vW.xz * 2.0), vec2(12.9898, 78.233))) * 43758.5453) * (0.6 + 0.4 * sin(time * 3.0 + vW.x)));
        c += vec3(1.0) * glit * path * 0.5;
        // Foam where the sea meets the glass: a thin lace, moving.
        float lace = 0.5 + 0.5 * sin(vW.x * 5.0 + vW.z * 3.0 + time * 1.4) * sin(vW.z * 4.0 - vW.x * 2.0 - time * 1.1);
        float foam = (1.0 - smoothstep(0.0, 0.3, abs(d - edge))) * (0.45 + 0.55 * lace);
        c = mix(c, vec3(0.96, 0.98, 1.0), foam * 0.7);
        // The wave coming in: a white line closing on the rim.
        if (wave >= 0.0) {
          float at = edge + (1.0 - wave) * edge * 1.4;
          float line = 1.0 - smoothstep(0.0, 0.7, abs(d - at));
          c = mix(c, vec3(1.0), line * 0.85);
        }
        c *= mix(1.0, 0.32, night);
        gl_FragColor = vec4(c, 1.0);
        #include <colorspace_fragment>
      }`,
  });

/** The water in the canals: dark where dry, a silver thread where wet, its head brighter; red where a line cut it. */
const threadMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -5,
    polygonOffsetUnits: -5,
    uniforms: { time: { value: 0 }, water: { value: new THREE.Color('#a9cbe4') }, cut: { value: RED.clone() } },
    vertexShader: /* glsl */ `
      attribute float aWet;
      attribute float aCut;
      attribute float aS;
      varying float vWet;
      varying float vCut;
      varying float vS;
      varying vec2 vUv;
      void main() {
        vWet = aWet;
        vCut = aCut;
        vS = aS;
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform vec3 water;
      uniform vec3 cut;
      varying float vWet;
      varying float vCut;
      varying float vS;
      varying vec2 vUv;
      void main() {
        float across = 1.0 - abs(vUv.y - 0.5) * 2.0;
        float wet = clamp(vWet, 0.0, 1.0);
        float head = clamp(vWet - 1.0, 0.0, 1.0);
        // Running water: ripples carried down the canal, a bright crest down its middle, foam at the head.
        float run = 0.5 + 0.5 * sin(vS * 14.0 - time * 9.0 + vUv.y * 3.0);
        float crest = smoothstep(0.55, 1.0, across);
        float a = smoothstep(0.0, 0.25, across) * wet * 0.92 + head * smoothstep(0.0, 0.2, across);
        vec3 c = mix(water * 0.8, water, run) + vec3(0.35) * crest * wet;
        c = mix(c, vec3(1.0), head * (0.7 + 0.3 * run));
        float burn = vCut * smoothstep(0.0, 0.6, across) * (0.6 + 0.4 * sin(time * 6.0 + vS * 3.0));
        c = mix(c, cut, clamp(burn * 1.2, 0.0, 1.0));
        gl_FragColor = vec4(c, clamp(max(a, burn * 0.85), 0.0, 1.0));
      }`,
  });

/** A pool's water: a pale disc with a slow ripple and a bright rim. */
const poolMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { time: { value: 0 }, lit: { value: 0 }, night: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform float lit;
      uniform float night;
      varying vec2 vUv;
      void main() {
        vec2 p = vUv - 0.5;
        float r = length(p) * 2.0;
        float ring = sin(r * 18.0 - time * 2.2) * 0.5 + 0.5;
        vec3 c = mix(vec3(0.66, 0.76, 0.84), vec3(0.95, 0.98, 1.0), 0.35 + 0.25 * ring * (1.0 - r));
        c = mix(c, vec3(1.0), smoothstep(0.8, 1.0, r) * 0.5);
        c += vec3(1.0, 0.36, 0.26) * lit * (1.0 - smoothstep(0.0, 0.5, r)) * 0.35;
        c *= mix(1.0, 0.5, night);
        gl_FragColor = vec4(c, 0.9 * (1.0 - smoothstep(0.96, 1.0, r)));
      }`,
  });

/** Stars on the glass at night: a sheet over the ground with points of cold light, as if one walked on the sky. */
const starSheetMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    uniforms: { time: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vXZ;
      void main() {
        vec4 w = modelMatrix * vec4(position + normal * 0.02, 1.0);
        vXZ = w.xz;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      varying vec2 vXZ;
      float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      void main() {
        vec2 p = vXZ * 2.2;
        vec2 i = floor(p);
        vec2 f = fract(p) - 0.5;
        float s = h(i);
        vec2 o = vec2(h(i + 3.1), h(i + 7.7)) - 0.5;
        float d = length(f - o * 0.6);
        float star = step(0.86, s) * (1.0 - smoothstep(0.0, 0.07 + 0.05 * s, d));
        float tw = 0.55 + 0.45 * sin(time * (1.0 + s * 3.0) + s * 40.0);
        vec3 c = mix(vec3(0.62, 0.72, 1.0), vec3(1.0), s);
        gl_FragColor = vec4(c * star * tw, star * tw * 0.8);
      }`,
  });

/** One stone of the canals, drawn where it sits: in the mouth it shuts (a fork), across the lip or beside it (the mouth). */
interface StoneView {
  mesh: THREE.Mesh;
  at: THREE.Vector3;
  /** Where it sits for each position. */
  spots: [THREE.Vector3, THREE.Vector3];
  now: THREE.Vector3;
}

export class AltarsLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(96);
  /** The glass's uniforms: the sky caught in it (by the hour), its clock. */
  private glassU = { uSky: { value: new THREE.Color('#8fa0b6') }, uTime: { value: 0 } };
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private sea: THREE.Mesh | null = null;
  private seaU: THREE.ShaderMaterial | null = null;
  private thread: THREE.Mesh | null = null;
  private threadU: THREE.ShaderMaterial | null = null;
  /** In step with the thread's vertices: the canal cell (index into `rules.canal`) each one lies on. */
  private threadCell: Int16Array = new Int16Array(0);
  private wetAttr: THREE.BufferAttribute | null = null;
  /** Where the running water's head is (null: none), for its glow. */
  private head: THREE.Vector3 | null = null;
  private cutAttr: THREE.BufferAttribute | null = null;
  private wetKey = '';
  private stones: StoneView[] = [];
  private pools: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; base: THREE.Vector3; depth: number; level: number; lit: number }[] = [];
  private eyes: { at: THREE.Vector3; mesh: THREE.Mesh; mat: THREE.MeshStandardMaterial; open: number }[] = [];
  private burns: THREE.InstancedMesh | null = null;
  private cone: THREE.Mesh | null = null;
  private coneMat: THREE.MeshBasicMaterial | null = null;
  private stars: THREE.Points | null = null;
  /** The watchtower's lit windows and its lantern on top; the molten ballistas' embers (world points). */
  private lamps: THREE.Vector3[] = [];
  private embers: THREE.Vector3[] = [];
  private hour: Hour = 'day';
  private now = 0;
  private waveAt = -1;
  private waveTick = -1;
  private tickNow = 0;
  /** The close's shore: its pools full and its eyes open from the start. */
  private lit = false;
  /** Where the HUD hangs its marks (world points): the stones, the pools, the altars. */
  readonly anchors: { stones: THREE.Vector3[]; pools: THREE.Vector3[]; altars: THREE.Vector3[] } = { stones: [], pools: [], altars: [] };

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `altars-${place}`;
    const kit = new Kit();
    const r = s.rules?.senda?.canal;
    // (A fight's canals open with the eye shut; only a scene's, the close, are built with it open.)
    this.lit = !!s.senda?.canal && s.senda.canal.lit >= 0;
    if (place === 'glassShore' && r) this.buildShore(kit, s, r);
    else if (place === 'watchtower') this.buildTower(kit);
    else if (place === 'temple' || place === 'keepYard') this.buildYard(kit, place === 'keepYard');
    if (place === 'glassShore' || place === 'watchtower' || place === 'glassNight') this.glassEdge(kit, place === 'glassShore');
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.05, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'altars-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    this.group.add(this.glow.mesh);
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild, hour: Hour = 'day'): void {
    this.terrain = terrain;
    const glass = this.place !== 'temple' && this.place !== 'keepYard';
    const c = new THREE.Color();
    const p = new THREE.Vector3();
    if (glass) {
      // One sheet of glass, not tiles: the seams closed, the colour black with the old light and shade kept.
      smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
      const board = this.map.radius - 2 + 0.5;
      const tint = (mesh: THREE.Mesh, wetNear: boolean): void => {
        const geo = mesh.geometry;
        const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
        const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
        const nor = geo.getAttribute('normal') as THREE.BufferAttribute | undefined;
        if (!col || !pos) return;
        mesh.updateMatrixWorld(true);
        for (let i = 0; i < pos.count; i++) {
          p.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
          // A tile's side (its face upright): the dark of the glass below, so no pale hex teeth show at the edge.
          if (nor && Math.abs(nor.getY(i)) < 0.5) {
            col.setXYZ(i, 0.02, 0.02, 0.03);
            continue;
          }
          // The walkable board a lighter, smoother glass (where one stands reads at once); past its rim dark, with a
          // ridge raised at the edge.
          const k = 0.5 + 0.25 * Math.sin(p.x * 0.35 + p.z * 0.2) + 0.25 * Math.sin(p.z * 0.45 - p.x * 0.15);
          const hq = p.x / S - p.z / (S * 0.866) / 2;
          const hr = p.z / (S * 0.866);
          const hd = Math.max(Math.abs(hq), Math.abs(hr), Math.abs(hq + hr));
          // The walkable glass fades into the dark beyond along a soft, wandering line (a hex-shaped edge read as the
          // board's zigzag, docs/RENDIMIENTO.md is not why: the shot was).
          const rd = Math.hypot(p.x, p.z) / S + 0.6 * Math.sin(Math.atan2(p.z, p.x) * 5 + 1.3) + 0.35 * Math.sin(p.x * 0.4 - p.z * 0.3);
          const inside = 1 - Math.max(0, Math.min(1, (rd - (board - 1.4)) / 2.2));
          c.set('#0b0b10').lerp(new THREE.Color('#151720'), k);
          c.lerp(new THREE.Color(this.place === 'glassNight' ? '#1d2230' : '#2c3240').lerp(new THREE.Color('#3a4252'), k), inside);
          // Toward the sea the glass is wet: lighter and bluer.
          if (wetNear) {
            const wet = Math.max(0, Math.min(1, (hd - (board - 1.6)) / 1.2)) * (p.z < 3.5 * S * 0.866 ? 1 : 0) * inside;
            c.lerp(new THREE.Color('#4a5668'), wet * 0.6);
          }
          col.setXYZ(i, c.r, c.g, c.b);
        }
        col.needsUpdate = true;
      };
      // The rim's rock tiles laid down to the glass (they stood as a hex-toothed wall round the board).
      {
        const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
        terrain.mesh.updateMatrixWorld(true);
        const inv = terrain.mesh.matrixWorld.clone().invert();
        const base = this.ctx.ground(0, 0);
        const v = new THREE.Vector3();
        for (let i = 0; i < pos.count; i++) {
          v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
          const hq = v.x / S - v.z / (S * 0.866) / 2;
          const hr = v.z / (S * 0.866);
          if (Math.max(Math.abs(hq), Math.abs(hr), Math.abs(hq + hr)) < board + 0.2) continue;
          v.y = base;
          v.applyMatrix4(inv);
          pos.setXYZ(i, v.x, v.y, v.z);
        }
        // The shore: toward the sea the glass slopes under the water along a wandering line, so the sea's edge on it
        // is a coast, not the board's hexagon.
        if (this.place === 'glassShore') {
          const seaZ = 3.4 * 0.866 * S;
          for (let i = 0; i < pos.count; i++) {
            v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
            const rd = Math.hypot(v.x, v.z) / S + 0.45 * Math.sin(Math.atan2(v.z, v.x) * 7 + 0.7) + 0.25 * Math.sin(v.x * 0.9 + v.z * 0.5);
            const seaward = Math.max(0, Math.min(1, (seaZ - v.z) / (1.2 * S)));
            const drop = Math.max(0, Math.min(0.5, (rd - (board - 1.1)) * 0.32)) * seaward;
            if (drop <= 0) continue;
            v.y -= drop;
            v.applyMatrix4(inv);
            pos.setXYZ(i, v.x, v.y, v.z);
          }
        }
        pos.needsUpdate = true;
        terrain.mesh.geometry.computeVertexNormals();
      }
      tint(terrain.mesh, this.place === 'glassShore');
      const ground = glassMaterial(terrain.mesh.material as THREE.MeshStandardMaterial, this.glassU);
      this.mats.push(ground);
      terrain.mesh.material = ground;
      terrain.decor.traverse((o) => {
        if (o.name === 'outskirts-ground') {
          tint(o as THREE.Mesh, false);
          // The sea's side of the world beyond the board goes under the water.
          if (this.place === 'glassShore') this.sink(o as THREE.Mesh);
          (o as THREE.Mesh).material = ground;
        } else if (o.name === 'roads') o.visible = false;
        const im = o as THREE.InstancedMesh;
        if (!im.isInstancedMesh) return;
        // No woods on the glass, and none of the board's loose rocks (they read as debris on a phone): the place's own
        // spires stand far out.
        // (Nothing of the board's instanced dressing: on the glass it read as pale teeth along the board's edge.)
        o.visible = false;
      });
      if (this.place === 'glassShore') {
        this.carveSea(terrain);
        this.buildSea();
      }
      // Open ground to the horizon: no fog bank over the glass or the water (only the scene's own haze far off).
      if (terrain.fog) terrain.fog.group.visible = false;
      if (this.place === 'glassNight') this.starSheet(terrain.mesh);
    } else {
      // The bastion's yard: cold grey flags, frost on what is beyond, no trees.
      const tint = (mesh: THREE.Mesh, flag: boolean): void => {
        const col = mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
        if (!col) return;
        for (let i = 0; i < col.count; i++) {
          const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
          if (flag) c.set(FLAG[i % 3] as string).multiplyScalar(0.72 + lum * 0.55);
          else c.set('#5d6460').lerp(new THREE.Color('#aab3bd'), 0.3).multiplyScalar(0.7 + lum * 0.5);
          col.setXYZ(i, c.r, c.g, c.b);
        }
        col.needsUpdate = true;
      };
      tint(terrain.mesh, true);
      terrain.decor.traverse((o) => {
        if (o.name === 'outskirts-ground') tint(o as THREE.Mesh, false);
        if ((o as THREE.InstancedMesh).isInstancedMesh && (o.name === 'trunks' || o.name === 'cones' || o.name === 'blobs' || o.name === 'tufts')) o.visible = false;
      });
    }
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    if (hour === 'night' || this.place === 'glassNight') this.starSky();
    this.paint(hour);
  }

  private recolor(im: THREE.InstancedMesh, palette: string[]): void {
    const m = new THREE.Matrix4();
    const p = new THREE.Vector3();
    const c = new THREE.Color();
    for (let i = 0; i < im.count; i++) {
      im.getMatrixAt(i, m);
      p.setFromMatrixPosition(m);
      const k = (Math.sin(p.x * 12.9 + p.z * 78.2) * 0.5 + 0.5) * 0.999;
      im.setColorAt(i, c.set(palette[Math.floor(k * palette.length)] as string));
    }
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
  }

  /** The outskirts on the sea's side go under the water (the far rim and both flanks, down to the sea's row). */
  private sink(mesh: THREE.Mesh): void {
    const pos = mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos) return;
    mesh.updateMatrixWorld(true);
    const v = new THREE.Vector3();
    const inv = mesh.matrixWorld.clone().invert();
    const seaZ = 3.5 * 0.866 * S;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      if (v.z > seaZ) continue;
      v.y = -0.9;
      v.applyMatrix4(inv);
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    pos.needsUpdate = true;
    mesh.geometry.computeVertexNormals();
  }

  /** The board's tiles that are sea (the rocks past the rim on the sea's side) cut out: the water shows there. */
  private carveSea(terrain: TerrainBuild): void {
    const geo = terrain.mesh.geometry;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos || geo.index) return;
    terrain.mesh.updateMatrixWorld(true);
    const m = terrain.mesh.matrixWorld;
    const v = new THREE.Vector3();
    const R = 7.4 * S;
    const seaZ = 3.5 * 0.866 * S;
    for (let i = 0; i < pos.count; i += 3) {
      let cx = 0;
      let cz = 0;
      for (let k = 0; k < 3; k++) {
        v.fromBufferAttribute(pos, i + k).applyMatrix4(m);
        cx += v.x;
        cz += v.z;
      }
      cx /= 3;
      cz /= 3;
      // Past the board's rim (hex distance in world: the larger of the three axes) on the sea's side.
      const q = cx / S - cz / (S * 0.866) / 2;
      const r = cz / (S * 0.866);
      const d = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
      if (d * S <= R || cz > seaZ) continue;
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);
      pos.setXYZ(i + 1, x, y, z);
      pos.setXYZ(i + 2, x, y, z);
    }
    pos.needsUpdate = true;
  }

  private buildSea(): void {
    const mat = seaMat();
    this.seaU = mat;
    this.mats.push(mat);
    const geo = new THREE.PlaneGeometry(420, 420, 1, 1).rotateX(-Math.PI / 2);
    this.geos.push(geo);
    const sea = new THREE.Mesh(geo, mat);
    sea.position.y = -0.06;
    sea.name = 'altars-sea';
    sea.renderOrder = 1;
    this.sea = sea;
    this.group.add(sea);
  }

  private starSheet(src: THREE.Mesh): void {
    const m = starSheetMat();
    this.mats.push(m);
    const sheet = new THREE.Mesh(src.geometry, m);
    sheet.matrixAutoUpdate = false;
    src.updateMatrixWorld(true);
    sheet.matrix.copy(src.matrixWorld);
    sheet.renderOrder = 2;
    sheet.frustumCulled = false;
    sheet.name = 'altars-starsheet';
    this.group.add(sheet);
    this.starU = m;
  }

  private starU: THREE.ShaderMaterial | null = null;

  /** A night sky of cold stars round the board (one Points draw). */
  private starSky(): void {
    const n = this.ctx.low ? 360 : 700;
    const pos = new Float32Array(n * 3);
    let a = 1234567;
    const rnd = (): number => ((a = (a * 1103515245 + 12345) >>> 0) / 4294967296);
    for (let i = 0; i < n; i++) {
      const th = rnd() * Math.PI * 2;
      const y = 0.08 + rnd() * 0.9;
      const rr = Math.sqrt(1 - y * y);
      pos[i * 3] = Math.cos(th) * rr * 100;
      pos[i * 3 + 1] = y * 100;
      pos[i * 3 + 2] = Math.sin(th) * rr * 100;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.geos.push(geo);
    const mat = new THREE.PointsMaterial({ color: '#cfdaff', size: 1.4, sizeAttenuation: false, transparent: true, opacity: 0.85, depthWrite: false, fog: false });
    this.mats.push(mat);
    const pts = new THREE.Points(geo, mat);
    pts.frustumCulled = false;
    pts.renderOrder = -9;
    this.stars = pts;
    this.group.add(pts);
  }

  paint(hour: Hour): void {
    this.hour = hour;
    const look = HOURS[hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) scene.fog.color.set(look.fog);
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    this.glassU.uSky.value.set(look.sky[1]).multiplyScalar(hour === 'night' ? 0.55 : hour === 'dusk' ? 0.75 : 0.62);
    // Open ground to a far horizon: the haze starts far out (the scene's fog is the world's; the place sets its reach).
    if (scene.fog instanceof THREE.Fog && this.place !== 'temple' && this.place !== 'keepYard') {
      scene.fog.near = 34;
      scene.fog.far = 150;
    }
    // The sun low on the side at dawn (the cold sunrise over the Burn).
    if (this.dome) this.dome.u.uSunDir.value.set(hour === 'dawn' ? 0.9 : 0.4, hour === 'dawn' ? 0.12 : 0.45, hour === 'dawn' ? -0.35 : -0.6);
    if (this.seaU) (this.seaU.uniforms.night as THREE.IUniform<number>).value = hour === 'night' ? 1 : hour === 'dusk' ? 0.5 : 0;
    for (const p of this.pools) (p.mat.uniforms.night as THREE.IUniform<number>).value = hour === 'night' ? 1 : 0;
    this.terrain?.fog?.tint({ low: look.low, mid: look.mid, far: look.bg, alpha: 1 });
  }

  /* ---------- the places ---------- */

  private cell(cell: number, h = 0): THREE.Vector3 {
    const c = cellCenter(this.map.radius, cell);
    return this.ctx.at(c.x, c.y, h);
  }

  /** Glass spires and broken plates round the board (and, on the shore, only inland: the sea is the rest). */
  private glassEdge(kit: Kit, shore: boolean): void {
    const spire = new THREE.ConeGeometry(0.22, 1, 5);
    const plate = new THREE.BoxGeometry(1, 0.12, 0.7);
    const R = this.map.radius - 1.2;
    let a = 99;
    const rnd = (): number => ((a = (a * 1103515245 + 12345) >>> 0) / 4294967296);
    for (let i = 0; i < 34; i++) {
      const th = rnd() * Math.PI * 2;
      const d = (R + 2.2 + rnd() * 4.5) * S;
      const x = Math.cos(th) * d;
      const z = Math.sin(th) * d;
      if (shore && z < 3.6 * 0.866 * S) continue;
      const y = this.ctx.ground(x, z);
      const tall = 1.2 + rnd() * 2.4;
      const col = GLASS_DARK[i % 3] as string;
      if (rnd() < 0.8) kit.add(spire, col, place(x, y + tall * 0.45, z, rnd() * 6, new THREE.Vector3(1 + rnd(), tall, 1 + rnd()), (rnd() - 0.5) * 0.4, (rnd() - 0.5) * 0.4));
      else kit.add(plate, col, place(x, y + 0.04, z, rnd() * 6, new THREE.Vector3(0.6 + rnd() * 1.2, 1, 0.6 + rnd()), (rnd() - 0.5) * 0.3, (rnd() - 0.5) * 0.5));
    }
    spire.dispose();
    plate.dispose();
  }

  /** An altar of the first Vigil: a block of light stone, its eye carved on the face toward `face`. */
  private altar(kit: Kit, at: THREE.Vector3, face: number, scale = 1): void {
    // A standing stone of the first Vigil on a low plinth: weathered pale stone, its top cut to a blunt point.
    const plinth = new THREE.CylinderGeometry(0.42 * scale, 0.48 * scale, 0.14 * scale, 6);
    const stele = new THREE.BoxGeometry(0.5 * scale, 0.98 * scale, 0.24 * scale);
    const top = new THREE.ConeGeometry(0.36 * scale, 0.26 * scale, 4);
    kit.add(plinth, '#7f858e', place(at.x, at.y + 0.07 * scale, at.z, face + Math.PI / 6));
    kit.add(stele, '#b3b8c0', place(at.x, at.y + 0.63 * scale, at.z, face));
    kit.add(top, '#c2c6cd', place(at.x, at.y + 1.25 * scale, at.z, face + Math.PI / 4, new THREE.Vector3(1, 1, 0.48)));
    // Weathering: a darker band at its foot.
    kit.add(new THREE.BoxGeometry(0.52 * scale, 0.16 * scale, 0.26 * scale), '#8d939b', place(at.x, at.y + 0.22 * scale, at.z, face));
    plinth.dispose();
    stele.dispose();
    top.dispose();
    // The eye high on its face: an almond carved in, its iris a disc that lights.
    const fx = Math.sin(face);
    const fz = Math.cos(face);
    const eyeAt = new THREE.Vector3(at.x + fx * 0.128 * scale, at.y + 0.86 * scale, at.z + fz * 0.128 * scale);
    const lid = new THREE.TorusGeometry(0.16 * scale, 0.028 * scale, 4, 18);
    lid.scale(1, 0.5, 1);
    kit.add(lid, '#3a3d45', place(eyeAt.x, eyeAt.y, eyeAt.z, face));
    lid.dispose();
    const mat = new THREE.MeshStandardMaterial({ color: '#1c1d22', emissive: new THREE.Color('#000000'), roughness: 0.4 });
    this.mats.push(mat);
    const geo = new THREE.CircleGeometry(0.068 * scale, 16);
    this.geos.push(geo);
    const iris = new THREE.Mesh(geo, mat);
    iris.position.set(eyeAt.x + fx * 0.006, eyeAt.y, eyeAt.z + fz * 0.006);
    iris.rotation.y = face;
    this.group.add(iris);
    this.eyes.push({ at: eyeAt, mesh: iris, mat, open: 0 });
    this.anchors.altars.push(new THREE.Vector3(at.x, at.y + 1.5 * scale, at.z));
  }

  /** The glass shore: the canals and their stones, the pools, the altars, the high pool's rock. */
  private buildShore(kit: Kit, s: BattleState, r: CanalRules): void {
    const pt = (cell: number): THREE.Vector3 => this.cell(cell, 0);
    // The high pool's rock (the test): the crag's cells raised as a plinth of dark glass-rock.
    const high = r.pools.find((p) => p.high);
    if (high) {
      // The plinth under the pool and its eye, a lower ledge round them, a step down to the lip.
      const hex = new THREE.CylinderGeometry(0.58 * S, 0.66 * S, 1, 6);
      for (const c of [high.at, high.altar]) {
        const p = pt(c);
        kit.add(hex, '#24222a', place(p.x, p.y + 0.22, p.z, Math.PI / 6, new THREE.Vector3(1, 0.44, 1)));
        kit.add(hex, '#1a181f', place(p.x, p.y + 0.08, p.z, Math.PI / 6, new THREE.Vector3(1.25, 0.16, 1.25)));
      }
      hex.dispose();
    }
    // The grooves and the water: one ribbon a canal, along its cells from where it starts.
    const from = new Map<number, number>();
    r.stones.forEach((st) => {
      for (const e of st.out) if (!from.has(e)) from.set(e, st.at);
    });
    const grooveP: number[] = [];
    const grooveI: number[] = [];
    const threadP: number[] = [];
    const threadUv: number[] = [];
    const threadS: number[] = [];
    const cells: number[] = [];
    const lift = (h: number): number => h + 0.035;
    const ribbon = (pts: THREE.Vector3[], owner: number[], w: number, outP: number[], uv: number[] | null, sv: number[] | null): void => {
      const n = pts.length;
      const base = outP.length / 3;
      for (let i = 0; i < n; i++) {
        const a = pts[Math.max(0, i - 1)] as THREE.Vector3;
        const b = pts[Math.min(n - 1, i + 1)] as THREE.Vector3;
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const l = Math.hypot(dx, dz) || 1;
        const nx = -dz / l;
        const nz = dx / l;
        const p = pts[i] as THREE.Vector3;
        outP.push(p.x + nx * w, lift(p.y), p.z + nz * w, p.x - nx * w, lift(p.y), p.z - nz * w);
        if (uv) uv.push(i / Math.max(1, n - 1), 0, i / Math.max(1, n - 1), 1);
        if (sv) sv.push(i * 0.25, i * 0.25);
        cells.push(owner[i] as number, owner[i] as number);
      }
      for (let i = 0; i < n - 1; i++) {
        const k = base + i * 2;
        grooveI.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
      }
    };
    const idx = (cell: number): number => {
      let lo = 0;
      let hi = r.canal.length - 1;
      while (lo <= hi) {
        const m = (lo + hi) >> 1;
        const x = r.canal[m] as number;
        if (x === cell) return m;
        if (x < cell) lo = m + 1;
        else hi = m - 1;
      }
      return -1;
    };
    const lines: { pts: THREE.Vector3[]; owner: number[] }[] = [];
    r.edges.forEach((e, ei) => {
      const nodes: number[] = [];
      const start = from.get(ei);
      if (start !== undefined) nodes.push(start);
      nodes.push(...e.cells);
      const pts: THREE.Vector3[] = [];
      const owner: number[] = [];
      // From the sea: the inlet starts out past the rim, in the water.
      if (ei === r.inlet) {
        const a = pt(e.cells[0] as number);
        const out = a.clone().setY(0).normalize().multiplyScalar(1.1 * S);
        pts.push(new THREE.Vector3(a.x + out.x, a.y, a.z + out.z));
        owner.push(idx(e.cells[0] as number));
      }
      for (let i = 0; i < nodes.length - 1; i++) {
        const a = pt(nodes[i] as number);
        const b = pt(nodes[i + 1] as number);
        const steps = 6;
        for (let k = 0; k < steps; k++) {
          const t = k / steps;
          const p = a.clone().lerp(b, t);
          p.y = this.ctx.ground(p.x, p.z);
          pts.push(p);
          // The first half of a step is the cell it leaves (a fork's: the canal's first), the rest the next one.
          owner.push(idx((t < 0.5 && i > 0 ? nodes[i] : nodes[i + 1]) as number));
        }
      }
      const last = pt(nodes[nodes.length - 1] as number);
      // A canal back to the sea runs on past the rim into the water.
      if (e.to === 'sea') {
        const out = last.clone().setY(0).normalize().multiplyScalar(0.9 * S);
        pts.push(last, new THREE.Vector3(last.x + out.x, last.y, last.z + out.z));
        owner.push(idx(nodes[nodes.length - 1] as number), idx(nodes[nodes.length - 1] as number));
      } else {
        pts.push(last);
        owner.push(idx(nodes[nodes.length - 1] as number));
      }
      lines.push({ pts, owner });
    });
    // Corners cut (the canals were carved by hand, not ruled): two rounds of Chaikin over each way.
    for (const l of lines) {
      let pts = l.pts;
      let owner = l.owner;
      for (let k = 0; k < 2; k++) {
        const np: THREE.Vector3[] = [pts[0] as THREE.Vector3];
        const no: number[] = [owner[0] as number];
        for (let i = 0; i < pts.length - 1; i++) {
          const a = pts[i] as THREE.Vector3;
          const b = pts[i + 1] as THREE.Vector3;
          np.push(a.clone().lerp(b, 0.25), a.clone().lerp(b, 0.75));
          no.push(owner[i] as number, owner[i + 1] as number);
        }
        np.push(pts[pts.length - 1] as THREE.Vector3);
        no.push(owner[owner.length - 1] as number);
        pts = np;
        owner = no;
      }
      for (const q of pts) q.y = this.ctx.ground(q.x, q.z);
      l.pts = pts;
      l.owner = owner;
    }
    // The canals cut in the glass: a pale worked lip (a frosted cut that reads from the game's camera) and inside it
    // the dark bed the water runs on. (DoubleSide: a hand-made ribbon faces down or up by the canal's way; FrontSide
    // dropped half of them, docs/RENDIMIENTO.md.)
    const cut = (w: number, color: string, emissive: string, offset: number, name: string): void => {
      grooveP.length = 0;
      grooveI.length = 0;
      cells.length = 0;
      for (const l of lines) ribbon(l.pts, l.owner, w * S, grooveP, null, null);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(grooveP, 3));
      geo.setIndex(grooveI.slice());
      geo.computeVertexNormals();
      this.geos.push(geo);
      const glossy = name === 'altars-beds';
      const mat = new THREE.MeshStandardMaterial({ color, emissive: new THREE.Color(emissive), roughness: glossy ? 0.08 : 0.45, metalness: glossy ? 0.6 : 0.2, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: offset, polygonOffsetUnits: offset });
      this.mats.push(mat);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.name = name;
      mesh.receiveShadow = true;
      this.group.add(mesh);
    };
    // A wet stone groove, not a road: a soft shadow where the cut goes in, a bevelled lip in the glass's own colour (no
    // pale edge: pale edges read as a road's white lines), and a dark wet bed whose gloss takes the sky.
    cut(0.2, '#272b34', '#000000', -1, 'altars-trough');
    cut(0.15, '#3a404c', '#0a0d12', -2, 'altars-lips');
    cut(0.1, '#1b2431', '#0c121b', -3, 'altars-beds');
    // The water's ribbon, narrower, over the same ways.
    grooveI.length = 0;
    cells.length = 0;
    for (const l of lines) ribbon(l.pts, l.owner, 0.1 * S, threadP, threadUv, threadS);
    const tg = new THREE.BufferGeometry();
    tg.setAttribute('position', new THREE.Float32BufferAttribute(threadP, 3));
    tg.setAttribute('uv', new THREE.Float32BufferAttribute(threadUv, 2));
    tg.setAttribute('aS', new THREE.Float32BufferAttribute(threadS, 1));
    const n = threadP.length / 3;
    this.wetAttr = new THREE.BufferAttribute(new Float32Array(n), 1).setUsage(THREE.DynamicDrawUsage);
    this.cutAttr = new THREE.BufferAttribute(new Float32Array(n), 1).setUsage(THREE.DynamicDrawUsage);
    tg.setAttribute('aWet', this.wetAttr);
    tg.setAttribute('aCut', this.cutAttr);
    tg.setIndex(grooveI.slice());
    this.threadCell = Int16Array.from(cells);
    // The close: the canals full and still from the first frame (a line of quiet water between the two eyes).
    if (this.lit) (this.wetAttr.array as Float32Array).fill(0.9);
    this.geos.push(tg);
    const tm = threadMat();
    this.threadU = tm;
    this.mats.push(tm);
    const thread = new THREE.Mesh(tg, tm);
    thread.name = 'altars-water';
    thread.renderOrder = 3;
    thread.frustumCulled = false;
    this.thread = thread;
    this.group.add(thread);
    // The stones: a fork's sits in the mouth it shuts; the mouth's slab across the lip when shut, beside it when open.
    const sg = new THREE.DodecahedronGeometry(0.2 * S, 0);
    sg.scale(1.15, 0.75, 1);
    const slab = new THREE.BoxGeometry(0.42 * S, 0.3 * S, 0.12 * S);
    this.geos.push(sg, slab);
    r.stones.forEach((st, i) => {
      const at = pt(st.at);
      const towards = (e: number): THREE.Vector3 => {
        const first = (r.edges[e] as CanalRules['edges'][number]).cells[0] as number;
        const q = pt(first);
        return at.clone().lerp(q, 0.42);
      };
      let spots: [THREE.Vector3, THREE.Vector3];
      if (st.mouth >= 0) {
        // Open: set aside; shut: in the canal at the lip.
        const into = pt((r.pools[st.mouth] as CanalRules['pools'][number]).at);
        const side = new THREE.Vector3(into.z - at.z, 0, at.x - into.x).normalize().multiplyScalar(0.4 * S);
        spots = [at.clone().add(side), at.clone().lerp(into, 0.35)];
      } else spots = [towards(st.out[1]), towards(st.out[0])];
      const m = new THREE.MeshStandardMaterial({ color: STONE[i % 3], roughness: 0.82, flatShading: true });
      this.mats.push(m);
      const mesh = new THREE.Mesh(st.mouth >= 0 ? slab : sg, m);
      mesh.castShadow = true;
      const now = spots[(s.senda?.canal?.pos[i] ?? r.start[i] ?? 0) as 0 | 1].clone();
      mesh.position.copy(now).setY(this.ctx.ground(now.x, now.z) + 0.1 * S);
      if (st.mouth >= 0) mesh.lookAt(pt((r.pools[st.mouth] as CanalRules['pools'][number]).at).setY(mesh.position.y));
      mesh.name = `altars-stone-${i}`;
      this.group.add(mesh);
      this.stones.push({ mesh, at, spots, now });
      this.anchors.stones.push(at.clone().setY(at.y + 0.5));
    });
    // The pools: a basin of light stone sunk in the glass, the altar standing at its back (the spec's «una piedra con un
    // ojo abierto tallado, dentro de una pileta seca»); the water rises round the stone's foot, the eye over it.
    r.pools.forEach((pool, i) => {
      const lifted = pool.high ? 0.44 : 0;
      const pc = pt(pool.at).add(new THREE.Vector3(0, lifted, 0));
      const a = pt(pool.altar).add(new THREE.Vector3(0, lifted, 0));
      const c = pc.clone().lerp(a, 0.3);
      const ax = Math.atan2(a.x - pc.x, a.z - pc.z);
      const rx = 0.55 * S;
      const rz = 0.8 * S;
      // A kerb of cut stone round the basin, one piece (an elliptical ring, bevelled), a darker course under it.
      const ringShape = new THREE.Shape();
      ringShape.absellipse(0, 0, rx + 0.16 * S, rz + 0.16 * S, 0, Math.PI * 2, false, 0);
      const hole = new THREE.Path();
      hole.absellipse(0, 0, rx, rz, 0, Math.PI * 2, true, 0);
      ringShape.holes.push(hole);
      const kerb = new THREE.ExtrudeGeometry(ringShape, { depth: 0.08, bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.03, bevelSegments: 1, curveSegments: 28 });
      kerb.rotateX(-Math.PI / 2);
      kit.add(kerb, '#9ea4ae', place(c.x, c.y + 0.0, c.z, ax));
      const course = new THREE.ExtrudeGeometry(ringShape, { depth: 0.04, bevelEnabled: false, curveSegments: 28 });
      course.rotateX(-Math.PI / 2);
      kit.add(course, '#6f757e', place(c.x, c.y - 0.02, c.z, ax, new THREE.Vector3(1.04, 1, 1.04)));
      kerb.dispose();
      course.dispose();
      // The basin's floor, dark wet stone under the water.
      const floor = new THREE.CircleGeometry(1, 24).rotateX(-Math.PI / 2);
      kit.add(floor, '#2a2d34', place(c.x, c.y + 0.012, c.z, ax, new THREE.Vector3(rx * 0.96, 1, rz * 0.96)));
      floor.dispose();
      const g = new THREE.CircleGeometry(1, 28).rotateX(-Math.PI / 2);
      this.geos.push(g);
      const mat = poolMat();
      this.mats.push(mat);
      const w = new THREE.Mesh(g, mat);
      w.position.copy(c);
      w.rotation.y = ax;
      w.renderOrder = 2;
      w.name = `altars-pool-${i}`;
      w.userData.r = [rx * 0.94, rz * 0.94];
      this.group.add(w);
      this.pools.push({ mesh: w, mat, base: c.clone(), depth: 0.1, level: 0, lit: 0 });
      this.anchors.pools.push(c.clone().setY(c.y + 0.3));
      // The stone at the back of the basin, its eye toward the sea (over the pool).
      const stone = c.clone().lerp(a, 0.62);
      this.altar(kit, stone, Math.atan2(pc.x - a.x, pc.z - a.z), 0.95);
    });
    // The burnt lines and the plough's lanes (one instanced quad each).
    const q = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.geos.push(q);
    const bm = new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
    this.mats.push(bm);
    const burns = new THREE.InstancedMesh(q, bm, 96);
    burns.count = 0;
    burns.frustumCulled = false;
    burns.renderOrder = 4;
    burns.name = 'altars-burns';
    burns.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(96 * 3), 3);
    this.burns = burns;
    this.group.add(burns);
  }

  /** The watchtower of the first Vigil, its molten ballistas, the second altar in the middle; the drake's cone. */
  private buildTower(kit: Kit): void {
    const at = this.ctx.at(0, 0, 0);
    this.altar(kit, at, 0, 1.1);
    // The tower: past the board's far rim, tall, pale stone, its top broken.
    const tz = -7.4 * S;
    const tx = 1.2 * S;
    const ty = this.ctx.ground(tx, tz);
    // Pale ash blown over the glass in drifts (the Burn's open floor reads by them; the glass alone was a black hole).
    const drift = new THREE.CircleGeometry(1, 7).rotateX(-Math.PI / 2);
    let a = 7;
    const rnd = (): number => ((a = (a * 1103515245 + 12345) >>> 0) / 4294967296);
    for (let i = 0; i < 46; i++) {
      const th = rnd() * Math.PI * 2;
      const d = (7.4 + rnd() * 5) * S;
      const x = Math.cos(th) * d;
      const z = Math.sin(th) * d;
      kit.add(drift, i % 3 === 0 ? '#3d424b' : '#2c3038', place(x, this.ctx.ground(x, z) + 0.015, z, rnd() * 6, new THREE.Vector3(0.3 + rnd() * 0.7, 1, 0.12 + rnd() * 0.3)));
    }
    drift.dispose();
    const body = new THREE.CylinderGeometry(1.25, 1.45, 6.4, 12);
    const band = new THREE.CylinderGeometry(1.32, 1.32, 0.22, 12);
    const merlon = new THREE.BoxGeometry(0.42, 0.55, 0.32);
    kit.add(body, STONE[1] as string, place(tx, ty + 3.2, tz));
    kit.add(band, STONE[2] as string, place(tx, ty + 1.1, tz));
    kit.add(band, STONE[2] as string, place(tx, ty + 4.4, tz));
    for (let i = 0; i < 12; i++) {
      // Broken: some merlons gone, the rest leaning.
      if (i === 2 || i === 3 || i === 7) continue;
      const a = (i / 12) * Math.PI * 2;
      kit.add(merlon, STONE[i % 3] as string, place(tx + Math.cos(a) * 1.18, ty + 6.65 - (i % 4 === 0 ? 0.25 : 0), tz + Math.sin(a) * 1.18, -a, 1, (i % 5) * 0.04, 0));
    }
    // A dark doorway and a slit window toward the board.
    kit.add(new THREE.BoxGeometry(0.62, 1.15, 0.2), '#1a1b20', place(tx, ty + 0.58, tz + 1.36));
    kit.add(new THREE.BoxGeometry(0.16, 0.6, 0.2), '#1a1b20', place(tx, ty + 3.6, tz + 1.38));
    // Lit: a lantern in the doorway, the slit window, and a cold watch-fire on the broken top (glows, no lights).
    this.lamps.push(new THREE.Vector3(tx, ty + 0.75, tz + 1.5), new THREE.Vector3(tx, ty + 3.6, tz + 1.5), new THREE.Vector3(tx, ty + 7.0, tz));
    // Fallen stones at its foot.
    const rubble = new THREE.DodecahedronGeometry(0.3, 0);
    for (let i = 0; i < 9; i++) kit.add(rubble, STONE[i % 3] as string, place(tx - 1.6 + i * 0.42, ty + 0.12, tz + 1.6 + ((i * 37) % 7) * 0.12, i, 0.6 + (i % 3) * 0.25));
    // The molten ballistas: frames fused into black glass, their arms bent down, round the clearing.
    const beam = new THREE.BoxGeometry(0.14, 0.14, 1.3);
    const lump = new THREE.SphereGeometry(0.42, 7, 5);
    const spots: [number, number, number][] = [
      [-5.6, -3.4, 0.6],
      [5.9, -2.6, 2.2],
      [-6.4, 2.6, 1.1],
      [6.2, 3.4, 4.0],
      [-2.8, -6.6, 2.8],
    ];
    for (const [qx, qz, yaw] of spots) {
      const x = qx * S;
      const z = qz * S;
      const y = this.ctx.ground(x, z);
      // The glass it sank in, the frame's iron and wood gone grey, its arms bent down; an ember still in the glass.
      kit.add(lump, '#1a1820', place(x, y + 0.15, z, yaw, new THREE.Vector3(1.6, 0.6, 1.3)));
      kit.add(beam, '#6b625a', place(x, y + 0.5, z, yaw, new THREE.Vector3(1.4, 1.4, 1.3), 0.35, 0));
      kit.add(beam, '#5e564f', place(x + Math.cos(yaw) * 0.55, y + 0.42, z - Math.sin(yaw) * 0.55, yaw + 1.4, new THREE.Vector3(1.3, 1.3, 1.0), -0.5, 0.2));
      kit.add(beam, '#5e564f', place(x - Math.cos(yaw) * 0.55, y + 0.42, z + Math.sin(yaw) * 0.55, yaw - 1.4, new THREE.Vector3(1.3, 1.3, 1.0), -0.6, -0.2));
      kit.add(new THREE.CylinderGeometry(0.12, 0.12, 0.5, 8), '#3d3833', place(x, y + 0.75, z, yaw, 1, 0, Math.PI / 2));
      this.embers.push(new THREE.Vector3(x, y + 0.3, z));
    }
    // The drake's cone: a fan on the glass, shown while it draws breath.
    const segs = 18;
    const pos: number[] = [0, 0, 0];
    for (let i = 0; i <= segs; i++) {
      const a = -1 + (2 * i) / segs;
      pos.push(Math.cos(a), 0, Math.sin(a));
    }
    const idx: number[] = [];
    for (let i = 1; i <= segs; i++) idx.push(0, i + 1, i);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setIndex(idx);
    this.geos.push(g);
    const m = new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
    this.mats.push(m);
    const cone = new THREE.Mesh(g, m);
    cone.visible = false;
    cone.renderOrder = 4;
    cone.name = 'altars-cone';
    this.cone = cone;
    this.coneMat = m;
    this.group.add(cone);
  }

  /** The bastion's yard: the old altar stone; the keep behind it where there is no Temple. */
  private buildYard(kit: Kit, keep: boolean): void {
    const at = this.ctx.at(0, -1.2 * 866, 0);
    this.altar(kit, at, 0, 1.15);
    if (!keep) return;
    const kz = -5.6 * S;
    const ky = this.ctx.ground(0, kz);
    kit.add(new THREE.BoxGeometry(3.4, 5.6, 3.0), '#6d727b', place(0, ky + 2.8, kz));
    kit.add(new THREE.BoxGeometry(3.7, 0.3, 3.3), '#7d828b', place(0, ky + 5.7, kz));
    for (let i = 0; i < 5; i++) for (const sx of [-1, 1]) kit.add(new THREE.BoxGeometry(0.4, 0.5, 0.4), '#7d828b', place(sx * 1.6, ky + 6.05, kz - 1.4 + i * 0.7));
    kit.add(new THREE.BoxGeometry(0.9, 1.6, 0.2), '#1d1f24', place(0, ky + 0.8, kz + 1.52));
    for (const sx of [-1, 1]) kit.add(new THREE.BoxGeometry(0.22, 0.7, 0.2), '#1d1f24', place(sx * 0.9, ky + 3.6, kz + 1.52));
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    this.tickNow = s.tick;
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      if (e.kind === 'seaWarn') {
        this.waveAt = s.tick;
        this.waveTick = s.tick + (s.rules?.senda?.canal?.warn ?? 60);
      }
    }
  }

  draw(s: BattleState): void {
    const r = s.rules?.senda?.canal;
    const w = s.senda?.canal;
    if (r && w) {
      this.drawWater(s, r, w);
      this.stones.forEach((st, i) => {
        const want = st.spots[(w.pos[i] ?? 0) as 0 | 1];
        st.now.lerp(want, 0.35);
        st.mesh.position.set(st.now.x, this.ctx.ground(st.now.x, st.now.z) + 0.1 * S, st.now.z);
      });
      this.pools.forEach((p, i) => {
        const full = this.lit ? 1 : Math.min(1, (w.level[i] ?? 0) / Math.max(1, r.fill));
        p.level += (full - p.level) * 0.3;
        p.mesh.visible = p.level > 0.02;
        p.mesh.position.y = p.base.y + 0.02 + p.level * p.depth;
        const [rx, rz] = p.mesh.userData.r as [number, number];
        const k = 0.6 + 0.4 * p.level;
        p.mesh.scale.set(rx * k, 1, rz * k);
        // The small shore's eye opens with its pool full; the test's two when the water has gone with both full.
        p.lit = this.lit || w.lit >= 0 || (r.kind === 'small' && (w.level[i] ?? 0) >= r.fill) ? 1 : 0;
      });
      this.eyes.forEach((e, i) => {
        e.open += ((this.pools[i]?.lit ?? 0) - e.open) * 0.12;
      });
      this.drawBurns(s, r, w);
    }
    const k = s.rules?.senda?.kindle;
    if (k && this.eyes[0]) {
      // The second altar kindles as the fight's clock fills.
      const need = s.rules?.senda?.goal.need ?? 1;
      const done = s.result === 'won' ? 1 : Math.min(1, s.waveT / Math.max(1, need));
      const e = this.eyes[0];
      e.open = done;
    }
    if (this.cone && this.coneMat) {
      const c = kindleCone(s);
      this.cone.visible = !!c;
      if (c) {
        const p = this.ctx.at(c.x, c.y, 0.05);
        this.cone.position.copy(p);
        const ang = (c.aim / 1024) * Math.PI * 2;
        this.cone.rotation.y = -ang;
        const half = (c.arc / 1024) * Math.PI * 2;
        this.cone.scale.set(c.range * K, 1, Math.tan(half) * c.range * K * 0.95);
      }
    }
  }

  /** The canals' water: each vertex's wetness from its cell (the front's cell brighter), and the cut ones red. */
  private drawWater(s: BattleState, r: CanalRules, w: NonNullable<NonNullable<BattleState['senda']>['canal']>): void {
    const wa = this.wetAttr;
    const ca = this.cutAttr;
    if (!wa || !ca) return;
    const front = w.front ? ((r.edges[w.front.e] as CanalRules['edges'][number]).cells[w.front.i] ?? -1) : -1;
    const frontIdx = front >= 0 ? r.canal.indexOf(front) : -1;
    this.head = front >= 0 ? this.cell(front, 0.12) : null;
    // Only when something changed (a tick's wetness, the cuts): the key is cheap next to the upload.
    const key = `${s.tick}`;
    if (key === this.wetKey) return;
    this.wetKey = key;
    const arrW = wa.array as Float32Array;
    const arrC = ca.array as Float32Array;
    const lit = this.lit;
    for (let i = 0; i < this.threadCell.length; i++) {
      const c = this.threadCell[i] as number;
      if (c < 0) {
        arrW[i] = 0;
        arrC[i] = 0;
        continue;
      }
      const left = (w.wet[c] as number) - s.tick;
      arrW[i] = lit ? 0.85 : (left > 0 ? Math.min(1, left / Math.max(1, r.wet) + 0.25) : 0) + (c === frontIdx ? 1 : 0);
      arrC[i] = (w.cuts[c] as number) > s.tick ? 1 : 0;
    }
    wa.needsUpdate = true;
    ca.needsUpdate = true;
  }

  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();

  /** The plough's burnt cells (ember, fading) and, while its blades are down, its three lanes (red, pulsing). */
  private drawBurns(s: BattleState, r: CanalRules, w: NonNullable<NonNullable<BattleState['senda']>['canal']>): void {
    const b = this.burns;
    if (!b) return;
    let n = 0;
    const up = new THREE.Vector3(0, 1, 0);
    for (let i = 0; i < w.burn.length && n < 80; i++) {
      const left = (w.burnUntil[i] as number) - s.tick;
      if (left <= 0) continue;
      const p = this.cell(w.burn[i] as number, 0.03);
      const k = Math.min(1, left / Math.max(1, r.cut));
      this.q4.setFromAxisAngle(up, (w.burn[i] as number) * 1.7);
      this.m4.compose(p, this.q4, this.s3.set(0.86 * S, 1, 0.28 * S));
      b.setMatrixAt(n, this.m4);
      b.setColorAt(n, this.c3.copy(RED).lerp(new THREE.Color('#ffb070'), 0.25 * k).multiplyScalar(0.35 + 0.65 * k));
      n++;
    }
    const pl = w.plow;
    const P = r.plow;
    const e = pl.id && pl.mode === 1 && P ? s.enemies.find((x) => x.id === pl.id) : undefined;
    if (e && P) {
      const pulse = 0.55 + 0.45 * Math.sin(this.now * 14);
      const ang = Math.atan2(pl.dy, pl.dx);
      this.q4.setFromAxisAngle(up, -ang);
      for (const side of [-1, 0, 1]) {
        const ox = (-pl.dy * side * 1000) / 1000;
        const oy = (pl.dx * side * 1000) / 1000;
        const mid = this.ctx.at(e.x + ox + (pl.dx * P.len * 500) / 1000, e.y + oy + (pl.dy * P.len * 500) / 1000, 0.04);
        this.m4.compose(mid, this.q4, this.s3.set(P.len * S, 1, 0.12 * S));
        b.setMatrixAt(n, this.m4);
        b.setColorAt(n, this.c3.copy(RED).multiplyScalar(pulse));
        n++;
      }
    }
    b.count = n;
    b.instanceMatrix.needsUpdate = true;
    if (b.instanceColor) b.instanceColor.needsUpdate = true;
  }

  frame(now: number, camera?: THREE.Camera): void {
    this.now = now;
    this.dome?.frame(now, camera);
    if (this.stars && camera) this.stars.position.setFromMatrixPosition(camera.matrixWorld);
    if (this.starU) (this.starU.uniforms.time as THREE.IUniform<number>).value = now;
    this.glassU.uTime.value = now;
    if (this.threadU) (this.threadU.uniforms.time as THREE.IUniform<number>).value = now;
    for (const p of this.pools) {
      (p.mat.uniforms.time as THREE.IUniform<number>).value = now;
      (p.mat.uniforms.lit as THREE.IUniform<number>).value += (p.lit - (p.mat.uniforms.lit as THREE.IUniform<number>).value) * 0.08;
    }
    if (this.seaU) {
      (this.seaU.uniforms.time as THREE.IUniform<number>).value = now;
      // The wave rolling in over its warning (12 ticks a second), then gone.
      const span = this.waveTick - this.waveAt;
      const t = span > 0 && this.tickNow <= this.waveTick + 6 ? (this.tickNow - this.waveAt) / span : -1;
      (this.seaU.uniforms.wave as THREE.IUniform<number>).value = t >= 0 && t <= 1.1 ? Math.min(1, t) : -1;
    }
    if (this.coneMat && this.cone?.visible) this.coneMat.opacity = 0.42 + 0.22 * (0.5 + 0.5 * Math.sin(now * 12));
    // The eyes: dark stone shut; open, a white iris with the red of the Vigil round it, and its glow.
    this.glow.begin();
    // The water's head running down the canal: foam and a point of white light.
    if (this.head) {
      this.glow.add(this.head.x, this.head.y, this.head.z, WATER, 1.5, 0.75);
      this.glow.add(this.head.x, this.head.y, this.head.z, new THREE.Color('#ffffff'), 0.5, 1);
    }
    // The pools glow as they fill (more when their eye is open).
    for (const p of this.pools) if (p.level > 0.05) this.glow.add(p.base.x, p.base.y + 0.18, p.base.z, POOL_GLOW, 1.4 + 0.8 * p.level, (0.22 + 0.25 * p.lit) * p.level);
    // The watchtower's lights, cold, flickering a little; the embers in the molten ballistas, slow.
    this.lamps.forEach((l, i) => {
      const f = 0.85 + 0.15 * Math.sin(now * (5 + i) + i);
      this.glow.add(l.x, l.y, l.z, LAMP, i === 2 ? 3.2 : 1.6, (i === 2 ? 0.75 : 0.85) * f);
    });
    this.embers.forEach((e, i) => this.glow.add(e.x, e.y, e.z, RED, 1.3, 0.35 + 0.15 * Math.sin(now * 1.3 + i * 2)));
    for (const e of this.eyes) {
      const k = e.open;
      e.mat.emissive.copy(RED).lerp(new THREE.Color('#ffffff'), 0.55).multiplyScalar(k * 1.6);
      e.mat.color.set('#1c1d22').lerp(WATER, k);
      if (k > 0.03) {
        this.glow.add(e.at.x, e.at.y, e.at.z, RED_SOFT, 0.9 + 0.5 * k, 0.5 * k);
        this.glow.add(e.at.x, e.at.y, e.at.z, WATER, 0.45 + 0.2 * k, 0.65 * k);
      }
    }
    this.glow.end();
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
